import './Page.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';

import { useAppContext, useAppStaticContext } from '../AppContext';

export interface PageProps extends b2x.PageProps {
  assetOverlayColor?: string;
  background?: string;
  backgroundAsset?: b2x.contentTypes.AssetAsBackgroundContentSectionV2;
  backgroundSize?: string;
  headerCheckout?: boolean;
  noPaddingBottom?: boolean;
  noPaddingTop?: boolean;
  pageWrapperClassName?: string;
  transparentHeader?: boolean;
}

export const Page = ({
  assetOverlayColor,
  background,
  backgroundAsset,
  backgroundSize,
  children,
  className,
  headerCheckout = false,
  noPaddingBottom,
  noPaddingTop,
  pageWrapperClassName,
  transparentHeader = false,
  ...otherProps
}: PageProps) => {
  const { pageReady } = b2x.useAppContext();
  const { headerHeight } = useAppContext();
  const { setHeaderCheckout, setTransparentHeader } = useAppStaticContext();

  React.useEffect(() => {
    pageReady && setHeaderCheckout(headerCheckout);
    pageReady && setTransparentHeader(transparentHeader);
  }, [pageReady, headerCheckout, setHeaderCheckout, setTransparentHeader, transparentHeader]);

  const isMobile = b2x.untilBreakpoint('lg', b2x.getCurrentBreakpoint());
  // Transparent header should work only on desktop
  const isTransparentHeader = transparentHeader && headerHeight !== undefined && !isMobile;

  const _backgroundSize = (backgroundSize ?? 'contain') as 'initial' | 'cover' | 'contain' | undefined;

  return (
    <div
      className={classnames(pageWrapperClassName, 'page-wrapper h-100', {
        'asset-dark-overlay': Boolean(assetOverlayColor),
      })}
      style={
        {
          '--page-asset-dark-overlay-color': assetOverlayColor,
          marginTop: isTransparentHeader ? -headerHeight : undefined,
        } as React.CSSProperties
      }
    >
      <b2x.Page {...otherProps} className={classnames(className)}>
        <b2x.Div background={background} className="Page position-relative" display="grid">
          {backgroundAsset && (
            <div
              className={classnames('page-background-container', `background-type-${_backgroundSize}`)}
              style={{ gridColumn: '1', gridRow: '1' }}
            >
              <b2x.AssetAsBackgroundV2 {...backgroundAsset} backgroundSize={_backgroundSize} />
            </div>
          )}
          <b2x.Div
            className={classnames(
              'page-inner-container',
              'position-relative',
              { 'pb-3 pb-xl-5': !noPaddingBottom },
              { 'pt-0 pt-xl-0': noPaddingTop },
              { 'pb-0 pb-xl-0': noPaddingBottom }
            )}
            style={{
              gridColumn: '1',
              gridRow: '1',
              minWidth: '100%',
              // Transparent header should work only on desktop
              paddingTop: noPaddingTop || isMobile ? undefined : headerHeight,
              width: '100%',
            }}
          >
            {children}
          </b2x.Div>
        </b2x.Div>
      </b2x.Page>
    </div>
  );
};
